// =============================================================================
// SessionValidator - Validates server session on app load
// =============================================================================
// v3.2: Proactively validates session when app loads to catch expired sessions
// early rather than waiting for the first API call to fail.
// v6.0: Added Terms of Service acceptance check
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import { authApi } from '@/lib/auth';
import { TermsOfServiceOverlay } from './TermsOfServiceOverlay';
import { CURRENT_TERMS_VERSION } from '@/lib/terms-content';

interface SessionValidatorProps {
  children: React.ReactNode;
}

/**
 * SessionValidator component
 *
 * Validates the server session when the app loads. If localStorage says we're
 * authenticated but the server session is actually expired, this will:
 * 1. Clear the auth store
 * 2. Redirect to login
 *
 * Also checks if the user has accepted the current Terms of Service version.
 * If not, a blocking overlay is shown until they accept.
 */
export function SessionValidator({ children }: SessionValidatorProps) {
  const { step, logout, updateUser } = useAuthStore();
  const hasValidated = useRef(false);
  const [needsTermsAcceptance, setNeedsTermsAcceptance] = useState(false);

  useEffect(() => {
    // Only validate once per app load
    if (hasValidated.current) {
      return;
    }

    // Only validate if localStorage thinks we're authenticated
    if (step !== 'AUTHENTICATED') {
      return;
    }

    hasValidated.current = true;

    // Validate session with the server
    authApi.getMe()
      .then((response) => {
        if (!response.success) {
          // Session is invalid - clear state and redirect
          console.log('[SessionValidator] Session invalid, redirecting to login');
          logout();
          window.location.href = '/login';
          return;
        }

        // Update user in store with latest from server
        updateUser(response.user);

        // Check terms acceptance
        if (response.user.termsAcceptedVersion !== CURRENT_TERMS_VERSION) {
          setNeedsTermsAcceptance(true);
        }
      })
      .catch((error) => {
        // API call failed (likely 401) - clear state and redirect
        console.log('[SessionValidator] Session validation failed:', error.message);
        logout();
        window.location.href = '/login';
      });
  }, [step, logout, updateUser]);

  // Block app access until terms accepted
  if (needsTermsAcceptance) {
    return <TermsOfServiceOverlay onAccepted={() => setNeedsTermsAcceptance(false)} />;
  }

  return <>{children}</>;
}
